import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Navigation, NavigationProps } from '@/components/custom/Navigation';
import { SubNav, SubNavProps } from '@/components/custom/SubNav';
import useResponsive from '@/hooks/useResponsive';
import { 
  Loader2, 
  UserPlus, 
  Calendar, 
  DollarSign, 
  FileText, 
  CheckCircle 
} from 'lucide-react';

interface PageTemplateProps {
  children: React.ReactNode;
  className?: string;
  
  // Navigation configuration
  navigation?: Partial<NavigationProps>;
  showSubNav?: boolean;
  activeNavItem?: string;
  subNavData?: SubNavProps['navigationData'];
  
  // Page header configuration
  pageTitle?: string;
  pageDescription?: string;
  headerActions?: React.ReactNode;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  
  // Layout configuration
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '7xl' | 'full';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  backgroundColor?: string;
  
  // Loading state
  isLoading?: boolean;
  loadingMessage?: string;
}

/**
 * PageTemplate acts as the parent layout component for all pages
 * Provides consistent structure with Navigation, SubNav, and content slots
 * Fully responsive with smooth transitions and animations
 */
const PageTemplate: React.FC<PageTemplateProps> = ({
  children,
  className,
  navigation,
  showSubNav = true,
  activeNavItem = 'dashboard',
  subNavData,
  pageTitle,
  pageDescription,
  headerActions,
  breadcrumbs,
  maxWidth = '7xl',
  padding = 'md',
  backgroundColor = 'bg-gray-50',
  isLoading = false,
  loadingMessage = 'Loading...',
}) => {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [activeSection, setActiveSection] = useState('section1');
  const [isSubNavOpen, setIsSubNavOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Default navigation items - can be overridden via navigation prop
  const defaultNavItems = [
    { key: 'dashboard', label: 'Dashboard', href: '/dashboard' },
    { key: 'schedule', label: 'Schedule', href: '/schedule' },
    { key: 'patients', label: 'Patients', href: '/patients' },
    { key: 'billing', label: 'Billing', href: '/billing' },
    { key: 'reports', label: 'Reports', href: '/reports' },
  ];

  // Default SubNav sections - can be overridden via subNavData prop
  const defaultSubNavSections = subNavData || [
    {
      id: 'section1',
      title: 'Overview',
      items: [
        { id: 'item1', label: 'Dashboard', href: '#' },
        { id: 'item2', label: 'Analytics', href: '#' },
        { id: 'item3', label: 'Reports', href: '#' },
      ],
    },
    {
      id: 'section2',
      title: 'Management',
      items: [
        { id: 'item4', label: 'Patients', href: '#' },
        { id: 'item5', label: 'Appointments', href: '#' },
        { id: 'item6', label: 'Staff', href: '#' },
      ],
    },
    {
      id: 'section3',
      title: 'Financial',
      items: [
        { id: 'item7', label: 'Billing', href: '#' },
        { id: 'item8', label: 'Insurance', href: '#' },
        { id: 'item9', label: 'Claims', href: '#' },
      ],
    },
    {
      id: 'section4',
      title: 'Clinical',
      items: [
        { id: 'item10', label: 'Treatment Plans', href: '#' },
        { id: 'item11', label: 'Medical Records', href: '#' },
        { id: 'item12', label: 'Prescriptions', href: '#' },
      ],
    },
    {
      id: 'section5',
      title: 'Settings',
      items: [
        { id: 'item13', label: 'Practice Info', href: '#' },
        { id: 'item14', label: 'User Management', href: '#' },
        { id: 'item15', label: 'Preferences', href: '#' },
      ],
    },
  ];

  // Default activities for SubNav
  const defaultRecentActivities = [
    { 
      icon: UserPlus,
      action: 'New patient registered',
      description: 'Added to system',
      patient: 'Sarah Johnson',
      timestamp: '2 min ago'
    },
    { 
      icon: Calendar,
      action: 'Appointment confirmed',
      description: 'Scheduled for tomorrow',
      patient: 'Michael Chen',
      timestamp: '15 min ago'
    },
    { 
      icon: DollarSign,
      action: 'Payment received',
      description: '$250 copay',
      patient: 'Emily Davis',
      timestamp: '1 hour ago'
    },
    { 
      icon: FileText,
      action: 'Lab results available',
      description: 'Blood work complete',
      patient: 'Robert Wilson',
      timestamp: '2 hours ago'
    },
    { 
      icon: CheckCircle,
      action: 'Insurance claim approved',
      description: 'Claim #A1234',
      patient: 'Jennifer Martinez',
      timestamp: '3 hours ago'
    },
  ];

  const defaultActivityDetails = {
    icon: Calendar,
    action: 'Upcoming Appointment',
    description: 'Annual checkup',
    patient: 'John Doe',
    timestamp: 'Tomorrow at 10:00 AM',
    location: 'Room 203',
    date: 'Dec 15, 2024',
    provider: 'Dr. Smith',
    op: 'OP-12345'
  };

  // Determine SubNav variant based on viewport
  const getSubNavVariant = (): 'accordion' | 'sheet' | 'card' => {
    if (isMobile) return 'accordion';
    if (isTablet) return 'sheet';
    return 'card';
  };

  // Handle navigation item click for tablet/mobile sheet
  const handleNavItemClick = (key: string) => {
    if (isTablet || isMobile) {
      setIsSubNavOpen(true);
    }
  };

  // Get max width class
  const getMaxWidthClass = () => {
    const widthMap = {
      'sm': 'max-w-sm',
      'md': 'max-w-md',
      'lg': 'max-w-lg',
      'xl': 'max-w-xl',
      '2xl': 'max-w-2xl',
      '7xl': 'max-w-7xl',
      'full': 'max-w-full',
    };
    return widthMap[maxWidth] || 'max-w-7xl';
  };

  // Get padding class
  const getPaddingClass = () => {
    const paddingMap = {
      'none': '',
      'sm': 'px-2 sm:px-4 py-4 sm:py-6',
      'md': 'px-4 sm:px-6 lg:px-8 py-6 sm:py-8',
      'lg': 'px-6 sm:px-8 lg:px-12 py-8 sm:py-12',
    };
    return paddingMap[padding] || paddingMap['md'];
  };

  // Loading overlay
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-sky-600 mx-auto mb-4" />
          <p className="text-gray-600">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen', backgroundColor, className)}>
      {/* Navigation with integrated SubNav */}
      <Navigation
        items={navigation?.items || defaultNavItems}
        activeKey={navigation?.activeKey || activeNavItem}
        logo={navigation?.logo}
        search={!isMobile ? {
          value: searchValue,
          placeholder: navigation?.search?.placeholder || 'Search...',
          onChange: (value) => {
            setSearchValue(value);
            navigation?.search?.onChange?.(value);
          },
        } : undefined}
        account={navigation?.account || {
          initials: 'JD',
          name: 'John Doe',
          role: 'Administrator'
        }}
        onNavigate={(key) => {
          handleNavItemClick(key);
          navigation?.onNavigate?.(key);
        }}
        subNav={showSubNav ? {
          navigationData: defaultSubNavSections,
          activeTab: activeNavItem,
          variant: getSubNavVariant(),
          isOpen: isSubNavOpen,
          onClose: () => setIsSubNavOpen(false),
          showActivities: !isMobile,
          recentActivities: navigation?.subNav?.recentActivities || defaultRecentActivities,
          activityDetails: navigation?.subNav?.activityDetails || defaultActivityDetails,
          onItemClick: (group, item) => {
            console.log('SubNav item clicked:', group, item);
            navigation?.subNav?.onItemClick?.(group, item);
          },
          onViewAllActivities: () => {
            console.log('View all activities');
            navigation?.subNav?.onViewAllActivities?.();
          },
          onOpenRecord: () => {
            console.log('Open record');
            navigation?.subNav?.onOpenRecord?.();
          },
          ...navigation?.subNav,
        } : undefined}
        {...navigation}
      />

      {/* Main Content Area with smooth transitions */}
      <main className={cn(
        'transition-all duration-300 ease-in-out',
        getPaddingClass(),
        getMaxWidthClass(),
        'mx-auto'
      )}>
        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <li key={index} className="flex items-center">
                  {index > 0 && (
                    <svg
                      className="h-4 w-4 text-gray-400 mx-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {crumb.href ? (
                    <a
                      href={crumb.href}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {crumb.label}
                    </a>
                  ) : (
                    <span className="text-gray-900 font-medium">{crumb.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Page Header */}
        {(pageTitle || pageDescription || headerActions) && (
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                {pageTitle && (
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {pageTitle}
                  </h1>
                )}
                {pageDescription && (
                  <p className="mt-2 text-sm sm:text-base text-gray-600">
                    {pageDescription}
                  </p>
                )}
              </div>
              {headerActions && (
                <div className="flex items-center gap-2 flex-wrap">
                  {headerActions}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Page Content Slot with animation */}
        <div className="animate-in slide-in-from-bottom-4 duration-500">
          {children}
        </div>
      </main>
    </div>
  );
};

export default PageTemplate;
export { PageTemplate };
